import { ReactNode } from "react";

export type MetricAccent = "blue" | "emerald" | "violet" | "amber";

export interface MetricCardProps {
  icon: ReactNode;
  label: string;
  value: number;
  /** Optional small subtitle below the value (e.g. "90% of all roles"). */
  sub?: string;
  accent: MetricAccent;
  /** While true, the value (and sub if present) render as shimmer skeletons. */
  isLoading?: boolean;
}

interface AccentStyles {
  iconBg:   string;
  iconText: string;
  divider:  string;
  /** very faint card tint */
  cardTint: string;
  /** label text color */
  label:    string;
  /** small dot before the label */
  dot:      string;
  /** skeleton bar for the number — tinted to match the accent */
  skelMain: string;
  /** skeleton bar for the subtitle — softer tint */
  skelSub:  string;
}

const accentMap: Record<MetricAccent, AccentStyles> = {
  blue: {
    iconBg:   "bg-blue-100",
    iconText: "text-blue-600",
    divider:  "bg-blue-200",
    cardTint: "bg-blue-50/40",
    label:    "text-blue-700",
    dot:      "bg-blue-500",
    skelMain: "bg-blue-200/70",
    skelSub:  "bg-blue-100",
  },
  emerald: {
    iconBg:   "bg-emerald-100",
    iconText: "text-emerald-600",
    divider:  "bg-emerald-200",
    cardTint: "bg-emerald-50/40",
    label:    "text-emerald-700",
    dot:      "bg-emerald-500",
    skelMain: "bg-emerald-200/70",
    skelSub:  "bg-emerald-100",
  },
  violet: {
    iconBg:   "bg-violet-100",
    iconText: "text-violet-600",
    divider:  "bg-violet-200",
    cardTint: "bg-violet-50/40",
    label:    "text-violet-700",
    dot:      "bg-violet-500",
    skelMain: "bg-violet-200/70",
    skelSub:  "bg-violet-100",
  },
  amber: {
    iconBg:   "bg-amber-100",
    iconText: "text-amber-600",
    divider:  "bg-amber-200",
    cardTint: "bg-amber-50/40",
    label:    "text-amber-700",
    dot:      "bg-amber-500",
    skelMain: "bg-amber-200/70",
    skelSub:  "bg-amber-100",
  },
};

/**
 * Compact, professional metric tile.
 *
 * Design notes:
 *   • Tight padding (px-4 py-3) — fits more cards on screen at once.
 *   • Solid 1px border, NO shadow — enterprise / spreadsheet aesthetic.
 *   • `rounded-md` — small radius keeps it crisp.
 *   • Single thin accent divider between label and number — only color cue.
 *   • Modest icon (32px square, soft tinted bg).
 *   • Number is text-2xl gray-900 — high contrast, no decoration.
 */
function MetricCard({ icon, label, value, sub, accent, isLoading }: MetricCardProps) {
  const c = accentMap[accent];

  return (
    <div className={`relative border border-gray-200 rounded-md hover:border-gray-300 transition-colors overflow-hidden ${c.cardTint}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Compact icon — softly pulses while loading */}
        <div
          className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${c.iconBg} ${c.iconText} ${isLoading ? "opacity-60 animate-pulse" : ""}`}
        >
          <div className="[&>*]:!w-4 [&>*]:!h-4">{icon}</div>
        </div>

        {/* Vertical accent divider */}
        <div className={`self-stretch w-px ${c.divider}`} aria-hidden="true" />

        {/* Text block */}
        <div className="min-w-0 flex-1">
          <p className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide truncate ${c.label}`}>
            <span className={`w-1 h-1 rounded-full ${c.dot}`} aria-hidden="true" />
            {label}
          </p>

          <div className="flex items-baseline gap-2 mt-1">
            {isLoading ? (
              <span
                className={`inline-block h-7 w-20 rounded-md animate-pulse ${c.skelMain}`}
                aria-hidden="true"
              />
            ) : (
              <span className="text-2xl font-semibold text-gray-900 tabular-nums leading-none">
                {value.toLocaleString()}
              </span>
            )}

            {/* Subtitle inline next to number — saves vertical space */}
            {isLoading ? (
              <span
                className={`inline-block h-3 w-24 rounded-full animate-pulse ${c.skelSub}`}
                aria-hidden="true"
              />
            ) : sub ? (
              <span className="text-[11px] text-gray-500 truncate">{sub}</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MetricCard;
